import { useTranslation } from 'react-i18next'
import { ExternalLink } from 'lucide-react'
import {
  useGetApiCompaniesCurrentServicemodulesUrlsServiceModuleId,
} from '@/api/generated/company-management-user/company-management-user'
import type { ServiceModuleModel, UrlServiceModuleModel } from '@/api/model'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'

interface Props {
  module: ServiceModuleModel
  onClose: () => void
}

/** Prepends a scheme if none is present — otherwise the browser opens the URL relatively. */
function toHref(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return ''
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
}

/**
 * Open dialog of a URL module: display only of the `url` with an "open" action.
 * Editing happens exclusively via the pencil → `EditUrlServiceModule`.
 */
export function UrlServiceModuleDialog({ module, onClose }: Props) {
  const { t } = useTranslation(['service-modules-url', 'service-modules', 'common'])
  const { data, isLoading, isError } = useGetApiCompaniesCurrentServicemodulesUrlsServiceModuleId(module.id)
  const detail = ((data as any)?.data ?? data) as UrlServiceModuleModel | undefined

  function handleOpen() {
    const href = toHref(detail?.url ?? '')
    if (href) window.open(href, '_blank', 'noopener,noreferrer')
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div
              className="size-7 rounded"
              style={{ backgroundColor: module.colorHex || '#64748b' }}
            />
            {module.name}
          </DialogTitle>
          <DialogDescription>{t('openUrl.description')}</DialogDescription>
        </DialogHeader>

        <LoaderGeneral
          isLoading={isLoading}
          isError={isError}
          isEmpty={!detail?.url}
          loadingMessage={t('openUrl.loading')}
          errorMessage={t('openUrl.loadError')}
          emptyMessage={t('openUrl.empty')}
        >
          <div className="flex flex-col gap-3">
            <a
              href={toHref(detail?.url ?? '')}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary underline break-all"
            >
              {detail?.url}
            </a>
            <div>
              <Button variant="outline" onClick={handleOpen}>
                <ExternalLink className="size-4 mr-1" />
                {t('openUrl.open')}
              </Button>
            </div>
          </div>
        </LoaderGeneral>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('buttons.close', { ns: 'common' })}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
